import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useCreateTalentDiagnosticsRecommendation,
  useDeleteTalentDiagnosticsRecommendation,
  useTalentDiagnosticsRecommendations,
  useUpdateTalentDiagnosticsRecommendation,
} from '../../../hooks/useTalentDiagnostics';

interface Props {
  definitionId: string;
}

export const TalentDiagnosticsRecommendationsTab: React.FC<Props> = ({ definitionId }) => {
  const { data: recommendations = [], isLoading } = useTalentDiagnosticsRecommendations(definitionId);
  const createMutation = useCreateTalentDiagnosticsRecommendation(definitionId);
  const updateMutation = useUpdateTalentDiagnosticsRecommendation(definitionId);
  const deleteMutation = useDeleteTalentDiagnosticsRecommendation(definitionId);

  const [form, setForm] = useState({
    title: '',
    overview: '',
    recommendedActions: '',
    expectedOutcome: '',
    minScore: '0',
    maxScore: '100',
  });
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetForm = () => {
    setForm({
      title: '',
      overview: '',
      recommendedActions: '',
      expectedOutcome: '',
      minScore: '0',
      maxScore: '100',
    });
    setEditingId(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!definitionId) {
      toast.error('Enter a definition ID first');
      return;
    }

    if (!form.title.trim()) {
      toast.error('Recommendation title is required');
      return;
    }

    const bodyJson = JSON.stringify({
      overview: form.overview.trim(),
      recommendedActions: form.recommendedActions
        .split('\n')
        .map(action => action.trim())
        .filter(Boolean),
      expectedOutcome: form.expectedOutcome.trim(),
    });

    const payload = {
      id: editingId ?? undefined,
      diagnosticDefinitionId: definitionId,
      title: form.title.trim(),
      bodyJson,
      minScore: Number(form.minScore || 0),
      maxScore: Number(form.maxScore || 100),
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }

    resetForm();
  };

  const startEdit = (
    recommendation: {
      id?: string;
      title?: string;
      bodyJson?: string;
      minScore?: number;
      maxScore?: number;
    }
  ) => {

    let overview = '';
    let recommendedActions = '';
    let expectedOutcome = '';

    try {
      const body = JSON.parse(
        recommendation.bodyJson || '{}'
      );

      overview = body.overview ?? '';

      recommendedActions = Array.isArray(
        body.recommendedActions
      )
        ? body.recommendedActions.join('\n')
        : '';

      expectedOutcome =
        body.expectedOutcome ?? '';
    } catch (error) {
      console.error(error);
    }

    setEditingId(recommendation.id ?? null);

    setForm({
      title: recommendation.title ?? '',
      overview,
      recommendedActions,
      expectedOutcome,
      minScore: String(recommendation.minScore ?? 0),
      maxScore: String(recommendation.maxScore ?? 100),
    });
  };

  const handleDelete = (id?: string) => {
    if (!id) return;
    if (window.confirm('Delete this recommendation?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5 text-left">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-strivre-burgundy" />
          <h4 className="text-base font-semibold text-slate-900">Manage recommendations</h4>
        </div>
        <p className="mt-1 text-sm text-slate-500">Add tailored recommendations that appear after diagnostics are reviewed.</p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Recommendation title</label>
              <input
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                placeholder="e.g. Improve onboarding"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Overview
              </label>

              <textarea
                value={form.overview}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    overview: e.target.value,
                  }))
                }
                rows={4}
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-3 text-slate-900"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Recommended Actions
              </label>

              <textarea
                value={form.recommendedActions}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    recommendedActions: e.target.value,
                  }))
                }
                rows={6}
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-3 text-slate-900"
                placeholder={`One action per line

            Review recruitment process
            Improve KPIs
            Enhance candidate experience`}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Expected Outcome
              </label>

              <textarea
                value={form.expectedOutcome}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    expectedOutcome: e.target.value,
                  }))
                }
                rows={4}
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-3 text-slate-900"
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Min score</label>
              <input
                type="number"
                value={form.minScore}
                onChange={(event) => setForm((current) => ({ ...current, minScore: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Max score</label>
              <input
                type="number"
                value={form.maxScore}
                onChange={(event) => setForm((current) => ({ ...current, maxScore: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              />
            </div>
          </div>
          <div className="rounded-2xl border border-strivre-lightnavy bg-slate-50 p-5">
            <h4 className="font-semibold text-slate-900">
              Recommendation Preview
            </h4>

            {form.overview && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Overview
                </p>
                <p className="text-slate-600">
                  {form.overview}
                </p>
              </div>
            )}

            {form.recommendedActions && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Recommended Actions
                </p>

                <ul className="mt-2 list-disc pl-5 text-slate-600 space-y-1">
                  {form.recommendedActions
                    .split('\n')
                    .filter(Boolean)
                    .map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                </ul>
              </div>
            )}

            {form.expectedOutcome && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Expected Outcome
                </p>
                <p className="text-slate-600">
                  {form.expectedOutcome}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" className="flex items-center gap-2 rounded-xl bg-strivre-burgundy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-900">
              <Plus size={16} />
              {editingId ? 'Save recommendation' : 'Create recommendation'}
            </button>
            {editingId ? (
              <button type="button" onClick={resetForm} className="rounded-xl border border-strivre-lightnavy px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5 text-left">
        <h4 className="text-base font-semibold text-slate-900">Current recommendations</h4>
        <p className="text-sm text-slate-500">These will be surfaced with the diagnostics report.</p>

        {isLoading ? (
          <div className="mt-4 rounded-2xl border border-dashed border-strivre-lightnavy bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            Loading recommendations...
          </div>
        ) : recommendations.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-strivre-lightnavy bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            No recommendations found for this definition yet.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {recommendations.map((recommendation) => (
              <div key={recommendation.id ?? `${recommendation.title}-${recommendation.minScore}-${recommendation.maxScore}`} className="flex items-center justify-between rounded-2xl border border-strivre-lightnavy bg-slate-50/60 p-4">
                <div>
                  <p className="font-semibold text-slate-900">{recommendation.title || 'Untitled recommendation'}</p>
                  <p className="text-sm text-slate-500">Score range: {recommendation.minScore ?? 0} - {recommendation.maxScore ?? 100}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => startEdit(recommendation)} className="rounded-xl border border-strivre-lightnavy bg-white p-2 text-slate-600 transition hover:bg-strivre-softgrey">
                    <Pencil size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(recommendation.id)} className="rounded-xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
